import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { designImages } from '../../data/designImages';

const skinTones = {
  original: null,
  light: 'rgb(250, 219, 188)',
  medium: 'rgb(224, 187, 149)',
  dark: 'rgb(192, 142, 105)',
  darker: 'rgb(155, 98, 62)',
  deep: 'rgb(89, 69, 57)',
};

const INCH = '\u2033';

function renderFractionalParts(parts) {
  return parts.map((part, index) => {
    if (part.includes('/')) {
      const [num, den] = part.split('/');
      return (
        <span key={index}>
          {index > 0 && ' '}
          <sup className="text-sm">{num}</sup>⁄<sub className="text-sm">{den}</sub>
        </span>
      );
    }
    return (
      <span key={index}>
        {index > 0 && ' '}
        {part}
      </span>
    );
  });
}

/** One side of a `width x height` size string (may include fractions like 4 1/4). */
function formatCrossDimensionSide(dimension) {
  const parts = dimension.trim().split(' ').filter(Boolean);
  return (
    <span className="whitespace-nowrap">
      {renderFractionalParts(parts)}
      {INCH}
    </span>
  );
}

/** Scalar sizes: optional leading W/H, then one or more numbers separated by - or –. */
function formatScalarSize(size) {
  const trimmed = size.trim();
  const wh = trimmed.match(/^([WH])\s*(.*)$/i);
  const prefix = wh ? `${wh[1].toUpperCase()} ` : '';
  const body = (wh ? wh[2] : trimmed).trim();
  if (!body) {
    return prefix ? <span className="whitespace-nowrap">{prefix.trim()}</span> : '';
  }

  const rangeParts = body.split(/\s*[-–]\s*/).map((p) => p.trim()).filter(Boolean);
  const segments = rangeParts.map((segment, segIndex) => {
    let s = segment;
    let approx = '';
    if (s.startsWith('~')) {
      approx = '~';
      s = s.slice(1).trim();
    }
    let plus = false;
    if (s.endsWith('+') && /[\d.]/.test(s)) {
      plus = true;
      s = s.slice(0, -1).trim();
    }
    const parts = s.split(/\s+/).filter(Boolean);
    const hasFraction = parts.some((p) => p.includes('/'));
    return (
      <span key={`seg-${segIndex}`} className="whitespace-nowrap">
        {approx}
        {hasFraction ? renderFractionalParts(parts) : s}
        {INCH}
        {plus ? '+' : ''}
      </span>
    );
  });

  const joined = segments.reduce((acc, seg, i) => {
    if (i === 0) return seg;
    return (
      <>
        {acc}
        <span key={`en-${i}`}>–</span>
        {seg}
      </>
    );
  });

  return (
    <span className="whitespace-nowrap">
      {prefix}
      {joined}
    </span>
  );
}

function formatSize(size) {
  if (size == null || size === '') return '';
  if (typeof size !== 'string') return '';

  let trimmed = size.trim();
  if (!trimmed) return '';

  trimmed = trimmed.replace(/\s+inches?\s*$/i, '');

  if (trimmed.includes(INCH) && !/\d\/\d/.test(trimmed)) {
    return <span className="whitespace-nowrap">{trimmed}</span>;
  }

  if (/\s*x\s*/i.test(trimmed)) {
    const dimensions = trimmed.split(/\s*x\s*/i).map((dimension) => formatCrossDimensionSide(dimension));
    return dimensions.reduce((acc, dim, i) => {
      if (i === 0) return dim;
      return (
        <>
          {acc} × {dim}
        </>
      );
    });
  }

  return formatScalarSize(trimmed);
}

function DesignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedTone, setSelectedTone] = useState('original');
  const [showTones, setShowTones] = useState(false);
  const popupRef = useRef(null);

  const design = designImages.find(d => d.id === parseInt(id));

  useEffect(() => {
    if (!design) {
      navigate('/designs');
    }
  }, [design, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setShowTones(false);
      }
    };

    if (showTones) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTones]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!design) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex flex-col items-center">
        <div className="relative max-w-[450px] w-full">
          <img
            src={design.image}
            alt={design.title}
            className="w-full h-auto select-none"
            style={{
              WebkitTouchCallout: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              pointerEvents: 'none',
            }}
          />
          {selectedTone !== 'original' && (
            <div 
              className="absolute inset-0 mix-blend-multiply"
              // className="absolute inset-0 mix-blend-multiply"
              style={{ backgroundColor: skinTones[selectedTone] }}
            />
          )}
        </div>
       
        {/* <div className="mt-4">
              <button
                onClick={() => navigate('/booking')}
                className="px-6 py-2 rounded-full border-2 border-black bg-black text-white font-medium hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Booking
              </button>
            </div> */}


        <div className="mt-8 space-y-4 text-center">
          <h1 className="text-2xl font-bold">{design.title}</h1>
          <div className="space-y-4">
            <div className="flex justify-center items-center gap-6">
              <p className="text-lg">
                <span className="font-bold">Recommended Size:</span> <span className="whitespace-nowrap">{formatSize(design.size)}</span>
              </p>
              <p className="text-lg">
                <span className="font-bold">Estimated Hours:</span> {design.hours}
              </p>
            </div>

            <div className="space-y-4 text-center">
              <p className="text-lg">
                <span className="font-bold">Price:</span> {design.price}
              </p>
            </div>

            <div className="relative mt-4 space-y-4">
          
          <div className="flex justify-center w-full">
            <button
              onClick={() => setShowTones(!showTones)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 flex items-center justify-center gap-2 active:bg-transparent focus:bg-transparent ring-0 focus:ring-0 active:ring-0 focus:outline-none active:outline-none"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              PREVIEW ON SKIN
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {showTones && (
            <div 
              ref={popupRef}
              className="fixed md:absolute bottom-0 md:bottom-auto md:top-full left-0 md:left-1/2 right-0 md:right-auto md:w-64 md:-translate-x-1/2 md:mt-2 bg-white p-4 shadow-lg rounded-t-lg md:rounded-lg z-50"
            >
              <div className="flex justify-between items-center gap-4">
                <button
                  onClick={() => setSelectedTone('original')}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${
                    selectedTone === 'original' ? 'ring-1 ring-offset-1 ring-black' : ''
                  }`}
                >
                  X
                </button>
                {Object.entries(skinTones).map(([tone, color]) => (
                  tone !== 'original' && (
                    <button
                      key={tone}
                      onClick={() => setSelectedTone(tone)}
                      className={`w-8 h-8 rounded-full relative overflow-hidden transition-transform hover:scale-110 ${
                        selectedTone === tone ? 'ring-1 ring-offset-1 ring-black' : ''
                      }`}
                    >
                      <div className="absolute inset-0 bg-white" />
                      <div 
                        className="absolute inset-0 mix-blend-multiply"
                        style={{ backgroundColor: color }}
                      />
                    </button>
                  )
                ))}
              </div>
            </div>
          )}
        </div>

            {design.imageDescription && (
              <div className="mt-6">
                <p className="text-md">
                  {design.imageDescription.split('. ').map((sentence, index, array) => (
                    <React.Fragment key={index}>
                      {sentence}
                      {index < array.length - 1 && (
                        <>
                          .<br />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            )}
            {design.altDescription && (
              <div className="mt-4">
                <p className="text-md">
                  {design.altDescription.split('. ').map((sentence, index, array) => (
                    <React.Fragment key={index}>
                      {sentence}
                      {index < array.length - 1 && (
                        <>
                          .<br />
                        </>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            )}
            {design.imageSecondary && (
              <div className="mt-6 flex justify-center">
                <div className="relative max-w-[450px] w-full">
                  <img 
                    src={design.imageSecondary} 
                    alt={design.title} 
                    className="w-full h-auto select-none"
                    style={{
                      WebkitTouchCallout: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {design.tags && design.tags.length > 0 && (
          <div className="my-12">
            <div className="flex flex-wrap justify-center gap-1">
              <span className="text-lg">Notes: </span>
              {design.tags.map((tag, index) => (
                <React.Fragment key={tag}>
                  <span className="text-lg">{tag}</span>
                  {index < design.tags.length - 1 && <span className="text-lg">, </span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4 text-center"> 
          <p className="text-sm text-gray-500 italic mt-2 text-center">
            * the preview shown on a range of skin tones are approximations.
            <br />
            * price is based on its original artwork size and estimated work hours.
            <br />
            * significant changes to the size or design may change the price.
          </p>
        </div>

        {/* Design Grid */}
        <div className="mt-16 w-full">
          <hr className="border-t border-gray-200 mb-6" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {designImages
              .filter(d => d.id !== design.id && d.status !== 'soldOut')
              .map((otherDesign) => (
                <div 
                  key={otherDesign.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/design/${otherDesign.id}`)}
                >
                  <div className="w-full pt-[100%] relative overflow-hidden">
                    <img
                      src={otherDesign.image}
                      alt={otherDesign.title}
                      className="absolute top-0 left-0 w-full h-full object-contain hover:scale-105 transition-transform duration-200"
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DesignDetails; 